import {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactElement,
} from 'react';
import utils from 'utils';

interface IAuthorizationContext {
  state: {
    isAuthorized: boolean;
    accountId: string;
    userId: string;
  };
  actions: {
    login: () => void;
    logout: () => void;
  };
}

const { sessionToken, rememberMe, authenticatedData } =
  utils.getLocalStorageByKey('sessionToken', 'rememberMe', 'authenticatedData');

const initialState = {
  isAuthorized:
    Boolean(sessionToken) && (rememberMe || !!utils.getUserSession()),
  accountId: authenticatedData?.baseUserInfo.accountId ?? '',
  userId: authenticatedData?.baseUserInfo?.userId,
};

const initialContext = {
  state: initialState,
  actions: {
    login: () => {},
    logout: () => {},
  },
};

const authorizationContext =
  createContext<IAuthorizationContext>(initialContext);

function AuthorizationProvider({ children }: { children: ReactElement }) {
  const [authState, updateAuthState] = useState(initialState);

  const value = useMemo(
    () => ({
      state: authState,
      actions: {
        // Store user auth credentials in the context. This only stores data. It
        // does not run any API requests.
        login() {
          const storageData = utils.getLocalStorageByKey('authenticatedData');
          const accountId =
            storageData.authenticatedData?.baseUserInfo?.accountId || '';
          const userId =
            storageData.authenticatedData?.baseUserInfo.userId || '';

          updateAuthState({
            isAuthorized: true,
            accountId,
            userId,
          });
          // Note: There is more auth data in localstorage. That data is not handled here.
        },

        logout() {
          updateAuthState({
            isAuthorized: false,
            accountId: '',
            userId: '',
          });
        },
      },
    }),
    [authState]
  );

  return (
    <authorizationContext.Provider value={value}>
      {children}
    </authorizationContext.Provider>
  );
}

export default AuthorizationProvider;

export const useAuthorizationContext = () => useContext(authorizationContext);
